import React, { memo, useCallback, useEffect, useState } from "react";
import { Handle, Position, useReactFlow } from "@xyflow/react";
import { SelectionOverlay } from "./SelectionOverlay";
import { Home } from "lucide-react";
import { getAdaptiveFontSize } from "../utils";
import doubleDoorIcon from "../../../assets/icons/double-door.svg";
import doubleDoorFillIcon from "../../../assets/icons/double-door-fill.svg";
import registrationFillIcon from "../../../assets/icons/registration-fill.svg";
import registrationLightIcon from "../../../assets/icons/registration-icon-light.svg";
import loungeFillIcon from "../../../assets/icons/lounge-fill.svg";
import conferenceFillIcon from "../../../assets/icons/conference-fill.svg";
import meetingFillIcon from "../../../assets/icons/meeting-fill.svg";
import diningFillIcon from "../../../assets/icons/dining-fill.svg";
import cafeFillIcon from "../../../assets/icons/cafe-fill.svg";
import barFillIcon from "../../../assets/icons/bar-fill.svg";
import restroomFillIcon from "../../../assets/icons/restroom-fill.svg";
import enterIcon from "../../../assets/icons/enter-icon-light.svg";
import exitIcon from "../../../assets/icons/exit-icon-light.svg";
import stairIcon from "../../../assets/icons/stair.svg";

export const GateNode = memo(({ data, selected, id, width, height }) => {
  const { getNode } = useReactFlow();
  const [nodePosition, setNodePosition] = useState({ x: 0, y: 0 });
  const nodeData = data;
  // Get position from React Flow node and update when node moves
  useEffect(() => {
    const updatePosition = () => {
      const node = getNode(id);
      if (node) {
        setNodePosition(node.position);
      }
    };
    updatePosition();
    const interval = setInterval(updatePosition, 100);
    return () => clearInterval(interval);
  }, [id, getNode]);
  // Default values - use React Flow's width/height if available
  const nodeWidth = width || nodeData.width || 60;
  const nodeHeight = height || nodeData.height || 40;
  const textColor = nodeData.textColor || "#ffffff";
  const fontFamily = nodeData.fontFamily || "Inter";
  const baseFontSize = nodeData.fontSize || 12;
  const borderRadius = nodeData.borderRadius || 8;
  const rotation = nodeData.rotation || 0;
  const elementType = nodeData.elementType;

  // Icon mapping for elements
  const iconMap = {
    "double-door": doubleDoorIcon,
    "entry-gate": enterIcon,
    "exit-gate": exitIcon,
    registration: registrationFillIcon,
    lounge: loungeFillIcon,
    conference: conferenceFillIcon,
    meeting: meetingFillIcon,
    dining: diningFillIcon,
    cafe: cafeFillIcon,
    bar: barFillIcon,
    restroom: restroomFillIcon,
    stair: stairIcon,
  };

  const isFilledIcon =
    nodeData.isFilled ?? nodeData.backgroundColor !== "transparent";
  const resolvedElementIcon =
    elementType === "registration" && !isFilledIcon
      ? registrationLightIcon
      : elementType === "double-door" && isFilledIcon
        ? doubleDoorFillIcon
      : elementType && iconMap[elementType]
        ? iconMap[elementType]
        : null;
  const elementIcon = resolvedElementIcon;
  const accentColor =
    nodeData.accentColor ||
    nodeData.prevBackgroundColor ||
    (nodeData.iconColor !== "#ffffff" ? nodeData.iconColor : null) ||
    (nodeData.backgroundColor !== "transparent" ? nodeData.backgroundColor : null) ||
    "#000000";
  const backgroundColor = elementIcon
    ? isFilledIcon
      ? accentColor
      : "transparent"
    : nodeData.backgroundColor || "#3b82f6";
  const iconColor = elementIcon
    ? isFilledIcon
      ? "#ffffff"
      : accentColor
    : nodeData.iconColor || "#000000";
  const iconVisualSize = Math.max(16, Math.min(nodeWidth, nodeHeight));
  const usesFullIconFrame = elementType === "double-door" || elementType === "stair";

  // Calculate proportional font size based on node dimensions and label length.
  const minDimension = Math.min(nodeWidth, nodeHeight);
  const calculatedFontSize = getAdaptiveFontSize({
    width: nodeWidth,
    height: nodeHeight,
    text: nodeData.label || "",
    base: Math.min(baseFontSize, minDimension / 3),
    min: 4,
    max: 20,
    padding: 6,
  });

  const handleResize = useCallback(
    (newWidth, newHeight, newPosition) => {
      const resizeEvent = new CustomEvent("nodeResize", {
        detail: {
          nodeId: id,
          nodeType: "gate",
          width: Math.round(newWidth),
          height: Math.round(newHeight),
          position: newPosition,
        },
      });
      window.dispatchEvent(resizeEvent);
    },
    [id],
  );

  const handleRotate = useCallback(
    (newRotation) => {
      const rotateEvent = new CustomEvent("nodeRotate", {
        detail: {
          nodeId: id,
          nodeType: "gate",
          rotation: newRotation,
        },
      });
      window.dispatchEvent(rotateEvent);
    },
    [id],
  );

  // Check if node is locked
  const isLocked = nodeData.locked || false;

  return (
    <div className="relative w-full h-full" data-id={id}>
      {/* NodeResizer/SelectionOverlay must be direct child of node for React Flow to work properly */}
      {!isLocked && selected && (
        <SelectionOverlay
          selected={selected || false}
          onRotate={handleRotate}
          onResize={handleResize}
          minWidth={40}
          minHeight={30}
          nodeId={id}
          width={nodeWidth}
          height={nodeHeight}
          rotation={rotation}
          nodeX={nodePosition.x}
          nodeY={nodePosition.y}
        />
      )}
      <div
        className={`flex flex-col items-center justify-center w-full h-full ${
          !elementIcon ? "hover:shadow-md border-2 border-black" : ""
        }`}
        style={{
          background:
            elementIcon
              ? "transparent"
              : backgroundColor === "transparent"
                ? "transparent"
                : backgroundColor,
          color: textColor,
          fontFamily: fontFamily,
          borderRadius: `${borderRadius}px`,
          transform: `rotate(${rotation}deg) scale(${nodeData.flippedH ? -1 : 1}, ${nodeData.flippedV ? -1 : 1})`,
          border: elementIcon ? "none" : undefined,
          transition: "box-shadow 0.2s ease", // Only transition shadow, NOT transform
          cursor: isLocked ? "default" : "pointer",
          pointerEvents: "all",
        }}
      >
        {elementIcon ? (
          <div
            style={{
              position: "relative",
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isFilledIcon && !usesFullIconFrame && (
              <div
                style={{
                  position: "absolute",
                  width: `${iconVisualSize}px`,
                  height: `${iconVisualSize}px`,
                  borderRadius: "9999px",
                  backgroundColor: accentColor,
                }}
              />
            )}
            <div
              style={{
                position: "relative",
                width: usesFullIconFrame ? "100%" : `${iconVisualSize}px`,
                height: usesFullIconFrame ? "100%" : `${iconVisualSize}px`,
                backgroundColor: usesFullIconFrame && isFilledIcon ? accentColor : iconColor,
                maskImage: `url(${elementIcon})`,
                WebkitMaskImage: `url(${elementIcon})`,
                maskRepeat: "no-repeat",
                WebkitMaskRepeat: "no-repeat",
                maskPosition: "center",
                WebkitMaskPosition: "center",
                maskSize: usesFullIconFrame ? "100% 100%" : "contain",
                WebkitMaskSize: usesFullIconFrame ? "100% 100%" : "contain",
              }}
              className="flex-shrink-0"
              aria-label={
                nodeData.label ||
                (["double-door-top-left", "double-door-bottom-left", "double-door-top-right", "double-door-bottom-right"].includes(id)
                  ? "Element - Double Door"
                  : "Door")
              }
            />
          </div>
        ) : (
          <>
            <Home
              style={{
                width: `${Math.max(12, calculatedFontSize * 1.2)}px`,
                height: `${Math.max(12, calculatedFontSize * 1.2)}px`,
              }}
              className="mb-1 flex-shrink-0"
            />
            {nodeData.label && (
              <span
                className="text-center leading-tight font-medium overflow-hidden text-ellipsis px-1 max-w-full"
                style={{ fontSize: `${calculatedFontSize}px` }}
              >
                {nodeData.label}
              </span>
            )}
          </>
        )}

        {/* Connection handles */}
        <Handle
          type="target"
          position={Position.Top}
          className="w-2 h-2 bg-background border-border opacity-0 hover:opacity-100"
        />

        <Handle
          type="source"
          position={Position.Bottom}
          className="w-2 h-2 bg-background border-border opacity-0 hover:opacity-100"
        />

        <Handle
          type="target"
          position={Position.Left}
          className="w-2 h-2 bg-background border-border opacity-0 hover:opacity-100"
        />

        <Handle
          type="source"
          position={Position.Right}
          className="w-2 h-2 bg-background border-border opacity-0 hover:opacity-100"
        />
      </div>
    </div>
  );
});
